import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import { useAppSettings } from "../contexts/AppSettingsContext";
import { Button } from "@/components/ui/button";
import { DynamicIcon } from "./shared/IconMap";
import { IntegrationStatusPill } from "./shared/IntegrationHealth";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  CalendarDays,
  Search,
  LogOut,
  BookOpen,
  ScanLine,
  Users,
  Package,
  Server,
  Settings,
  Paintbrush,
  Database,
  ClipboardList,
  ChevronDown,
  Plug,
  MessageSquare,
  FolderSync,
  Bell,
} from "lucide-react";

export default function Layout({ children, onLogout, userRole, isFeatureEnabled }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { settings } = useAppSettings();
  const menuRef = useRef(null);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const resp = await axios.get(`${API}/admin/machine-categories`, { headers: getAuthHeaders() });
        setCategories(resp.data || []);
      } catch { /* fallback empty */ }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    axios.get(`${API}/notifications/count`, { headers: getAuthHeaders() })
      .then(r => setUnreadCount(r.data.unread_count || 0)).catch(() => {});
    const interval = setInterval(() => {
      axios.get(`${API}/notifications/count`, { headers: getAuthHeaders() })
        .then(r => setUnreadCount(r.data.unread_count || 0)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (userRole !== "admin") return;
    const fetchStatus = async () => {
      try {
        const resp = await axios.get(`${API}/admin/integrations-status`, { headers: getAuthHeaders() });
        setIntegrationStatus(resp.data);
      } catch { /* silent */ }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [userRole]);

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  const isAdmin = userRole === "admin";
  const isSuperOrAbove = userRole === "admin" || userRole === "superintendent";
  const isTechOrAbove = isSuperOrAbove || userRole === "technician";

  const fe = (key) => !isFeatureEnabled || isFeatureEnabled(key);

  const categoryNavItems = categories
    .filter((cat) => fe(`cat_${cat.name}`))
    .map((cat) => ({
      path: `/machines/${cat.name.toLowerCase()}`,
      label: cat.label,
      icon: <DynamicIcon name={cat.icon || "Wrench"} size={18} />,
    }));

  const navItems = [
    { path: "/", label: "Dashboard", icon: <LayoutDashboard size={18} /> },
    ...(fe("blueprints") ? [{ path: "/blueprints", label: "Blueprints", icon: <FileText size={18} /> }] : []),
    ...categoryNavItems,
    ...(fe("machine_index") ? [{ path: "/machine-index", label: "Machine Index", icon: <BookOpen size={18} /> }] : []),
    ...(fe("work_orders") ? [{ path: "/work-orders", label: "Work Orders", icon: <ClipboardList size={18} /> }] : []),
    ...(fe("qr_scanner") ? [{ path: "/qr-scanner", label: "QR Scanner", icon: <ScanLine size={18} /> }] : []),
    ...(isTechOrAbove && fe("maintenance") ? [{ path: "/maintenance", label: "Maintenance", icon: <Calendar size={18} /> }] : []),
    ...(isTechOrAbove && fe("maintenance_calendar") ? [{ path: "/maintenance/calendar", label: "Maint. Calendar", icon: <CalendarDays size={18} /> }] : []),
    ...(isTechOrAbove && fe("network_scan") ? [{ path: "/network-scan", label: "Network Scan", icon: <Search size={18} /> }] : []),
    ...(isTechOrAbove && fe("parts_catalog") ? [{ path: "/parts", label: "Parts Catalog", icon: <Package size={18} /> }] : []),
    ...(isAdmin ? [{ path: "/users", label: "Users", icon: <Users size={18} /> }] : []),
    ...(isAdmin ? [{ path: "/machine-settings", label: "Machine Settings", icon: <Settings size={18} /> }] : []),
    ...(isAdmin ? [{ path: "/branding", label: "Branding", icon: <Paintbrush size={18} /> }] : []),
    ...(isAdmin ? [{ path: "/customization", label: "Customization", icon: <DynamicIcon name="Wrench" size={18} /> }] : []),
    ...(isAdmin ? [{ path: "/dashboard-builder", label: "Dashboard Builder", icon: <DynamicIcon name="LayoutDashboard" size={18} /> }] : []),
    ...(isAdmin ? [{ path: "/nav-builder", label: "Nav Builder", icon: <DynamicIcon name="Navigation" size={18} /> }] : []),
    ...(isAdmin ? [{ path: "/schedulers", label: "Schedulers", icon: <DynamicIcon name="Clock" size={18} /> }] : []),
    ...(isTechOrAbove && fe("ecm") ? [{ path: "/ecm", label: "Change Mgmt", icon: <DynamicIcon name="GitBranch" size={18} /> }] : []),
    ...(isTechOrAbove && fe("config_mgmt") ? [{ path: "/config-mgmt", label: "Config Mgmt", icon: <DynamicIcon name="Settings2" size={18} /> }] : []),
    ...(isTechOrAbove && fe("retention") ? [{ path: "/retention", label: "Retention", icon: <DynamicIcon name="Archive" size={18} /> }] : []),
    ...(isTechOrAbove && fe("procedures") ? [{ path: "/procedures", label: "Procedures", icon: <DynamicIcon name="ClipboardList" size={18} /> }] : []),
    ...(isTechOrAbove && fe("cap") ? [{ path: "/cap", label: "CAP", icon: <DynamicIcon name="ShieldAlert" size={18} /> }] : []),
    ...(isTechOrAbove && fe("qualifications") ? [{ path: "/qualifications", label: "Quals & Training", icon: <DynamicIcon name="GraduationCap" size={18} /> }] : []),
    ...(isTechOrAbove && fe("supplier_quality") ? [{ path: "/supplier-quality", label: "Suppliers", icon: <DynamicIcon name="Building2" size={18} /> }] : []),
    ...(isTechOrAbove && fe("audit_mgmt") ? [{ path: "/audit-mgmt", label: "Audit Mgmt", icon: <DynamicIcon name="ClipboardCheck" size={18} /> }] : []),
    ...(isTechOrAbove && fe("ai_search") ? [{ path: "/ai-search", label: "AI Search", icon: <DynamicIcon name="Sparkles" size={18} /> }] : []),
    ...(isTechOrAbove && fe("knowledge_graph") ? [{ path: "/knowledge-graph", label: "Knowledge Graph", icon: <DynamicIcon name="Network" size={18} /> }] : []),
    ...(isTechOrAbove && fe("predictive") ? [{ path: "/predictive", label: "Predictive Maint", icon: <DynamicIcon name="TrendingUp" size={18} /> }] : []),
    ...(fe("kpi_center") ? [{ path: "/kpi-center", label: "KPI Center", icon: <DynamicIcon name="BarChart3" size={18} /> }] : []),
    ...(isTechOrAbove && fe("compliance_hub") ? [{ path: "/compliance-hub", label: "Compliance Hub", icon: <DynamicIcon name="ShieldCheck" size={18} /> }] : []),
    ...(isTechOrAbove && fe("digital_twin") ? [{ path: "/digital-twin", label: "Digital Twin", icon: <DynamicIcon name="Box" size={18} /> }] : []),
    ...(fe("marketplace") ? [{ path: "/marketplace", label: "Marketplace", icon: <DynamicIcon name="Store" size={18} /> }] : []),
    { path: "/my-work", label: "My Work", icon: <DynamicIcon name="ListChecks" size={18} /> },
    ...(userRole === "admin" ? [{ path: "/system-admin", label: "System Admin", icon: <DynamicIcon name="Server" size={18} /> }] : []),
    ...(isSuperOrAbove ? [{ path: "/audit-trail", label: "Audit Trail", icon: <DynamicIcon name="Shield" size={18} /> }] : []),
    { path: "/compliance", label: "Compliance", icon: <DynamicIcon name="ShieldCheck" size={18} /> },
    ...(isAdmin ? [{ path: "/connectors", label: "Connectors", icon: <DynamicIcon name="Plug" size={18} /> }] : []),
    { path: "/about", label: "About", icon: <DynamicIcon name="BookOpen" size={18} /> },
  ];

  const integrationItems = isAdmin ? [
    { path: "/m365", label: "Microsoft 365", icon: <DynamicIcon name="LayoutGrid" size={18} /> },
    { path: "/sap-integration", label: "SAP S/4HANA", icon: <Database size={18} />, statusKey: "sap" },
    { path: "/ldap-config", label: "AD / LDAP", icon: <Server size={18} />, statusKey: "ldap" },
    { path: "/qms", label: "QMS", icon: <DynamicIcon name="ShieldCheck" size={18} />, statusKey: "qms" },
    { path: "/aveva-pi", label: "AVEVA PI", icon: <DynamicIcon name="Activity" size={18} />, statusKey: "pi" },
    { path: "/opcua", label: "OPC UA Gateway", icon: <DynamicIcon name="Gauge" size={18} /> },
    { path: "/forge-viewer", label: "DWG Viewer", icon: <DynamicIcon name="Box" size={18} /> },
    { path: "/power-automate", label: "Power Automate", icon: <DynamicIcon name="Zap" size={18} />, statusKey: "power_automate" },
    { path: "/primavera", label: "Primavera P6", icon: <DynamicIcon name="CalendarDays" size={18} />, statusKey: "primavera" },
  ] : [];

  const currentPage = navItems.find((item) => item.path === location.pathname)
    || integrationItems.find((item) => item.path === location.pathname);

  const sidebarBg = settings.sidebar_bg;
  const sidebarText = settings.sidebar_text;
  const sidebarActive = settings.sidebar_active;
  const sidebarBorder = settings.sidebar_border;

  return (
    <div className="min-h-screen bg-[#F5F5F4] flex flex-col">
      {/* Header Bar */}
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 h-14"
        style={{ backgroundColor: sidebarBg, borderBottom: `1px solid ${sidebarBorder}` }}
        data-testid="header-bar"
      >
        {/* Logo + Name */}
        <div className="flex items-center gap-3">
          {settings.logo_url && <img src={settings.logo_url} alt={settings.app_name} className="h-8" data-testid="header-logo" />}
          <span className="font-heading font-black text-sm text-white tracking-wide hidden sm:inline">{settings.app_name.toUpperCase()}</span>
          <span className="text-[10px] font-mono hidden sm:inline px-1.5 py-0.5" style={{ color: sidebarText, opacity: 0.5 }}>v{settings.app_version}</span>
          <span className="text-xs font-mono hidden md:inline" style={{ color: sidebarText }}>{settings.app_tagline}</span>
        </div>

        {/* Notification Bell + Navigation Toggle */}
        <div className="flex items-center gap-3" ref={menuRef}>
          {/* Notification Bell */}
          <div className="relative">
            <button onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) { axios.get(`${API}/notifications?limit=10`, { headers: getAuthHeaders() }).then(r => { setNotifications(r.data.notifications || []); setUnreadCount(r.data.unread_count || 0); }).catch(() => {}); } }} className="relative p-2 transition-colors" style={{ color: sidebarText }} data-testid="notif-bell">
              <Bell size={18} />
              {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center" data-testid="notif-count">{unreadCount > 9 ? "9+" : unreadCount}</span>}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-full mt-1 w-80 max-h-[60vh] overflow-y-auto shadow-lg z-50" style={{ backgroundColor: sidebarBg, border: `1px solid ${sidebarBorder}` }} data-testid="notif-dropdown">
                <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: `1px solid ${sidebarBorder}` }}>
                  <span className="text-xs font-bold" style={{ color: sidebarText }}>Notifications</span>
                  {unreadCount > 0 && <button onClick={() => { axios.post(`${API}/notifications/mark-all-read`, {}, { headers: getAuthHeaders() }).then(() => { setUnreadCount(0); setNotifications(n => n.map(x => ({ ...x, read: true }))); }); }} className="text-[10px] text-[#2563EB] font-bold" data-testid="mark-all-read">Mark all read</button>}
                </div>
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-xs" style={{ color: sidebarText, opacity: 0.5 }}>No notifications</div>
                ) : (
                  notifications.map(n => (
                    <div key={n.id} className={`px-3 py-2 text-xs cursor-pointer transition-colors ${n.read ? "opacity-60" : ""}`} style={{ borderBottom: `1px solid ${sidebarBorder}`, color: sidebarText }} onClick={() => { if (!n.read) { axios.post(`${API}/notifications/${n.id}/read`, {}, { headers: getAuthHeaders() }); setUnreadCount(c => Math.max(0, c - 1)); setNotifications(ns => ns.map(x => x.id === n.id ? { ...x, read: true } : x)); } if (n.link) navigate(n.link); setNotifOpen(false); }} data-testid={`notif-${n.id}`}>
                      <p className="font-bold text-[11px]">{n.title}</p>
                      <p className="text-[10px] mt-0.5" style={{ opacity: 0.7 }}>{n.message}</p>
                      <p className="text-[9px] mt-0.5" style={{ opacity: 0.4 }}>{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors"
              style={{
                backgroundColor: menuOpen ? sidebarActive : "transparent",
                color: menuOpen ? "#fff" : sidebarText,
                border: `1px solid ${sidebarBorder}`,
              }}
              data-testid="nav-toggle"
            >
              {currentPage?.icon}
              <span>{currentPage?.label || "Navigate"}</span>
              <ChevronDown size={16} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-64 max-h-[calc(100vh-80px)] overflow-y-auto shadow-lg"
                style={{ backgroundColor: sidebarBg, border: `1px solid ${sidebarBorder}` }}
                data-testid="nav-menu"
              >
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                      style={{
                        backgroundColor: isActive ? sidebarActive : "transparent",
                        color: isActive ? "#fff" : sidebarText,
                      }}
                      onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = sidebarBorder; e.currentTarget.style.color = "#fff"; } }}
                      onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = sidebarText; } }}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[\s./]/g, '-')}`}
                    >
                      {item.icon}
                      <span className="font-medium tracking-wide">{item.label}</span>
                      {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white" />}
                    </button>
                  );
                })}

                {/* Integrations Section */}
                {integrationItems.length > 0 && (
                  <>
                    <div className="px-4 pt-3 pb-1" style={{ borderTop: `1px solid ${sidebarBorder}` }}>
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: sidebarText, opacity: 0.6 }}>
                        <Plug size={12} />
                        Integrations
                      </div>
                    </div>
                    {integrationItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { navigate(item.path); setMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                          style={{
                            backgroundColor: isActive ? sidebarActive : "transparent",
                            color: isActive ? "#fff" : sidebarText,
                          }}
                          onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = sidebarBorder; e.currentTarget.style.color = "#fff"; } }}
                          onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = sidebarText; } }}
                          data-testid={`nav-${item.label.toLowerCase().replace(/[\s./]/g, '-')}`}
                        >
                          {item.icon}
                          <span className="font-medium tracking-wide">{item.label}</span>
                          <span className="ml-auto">{integrationStatus?.[item.statusKey] && <IntegrationStatusPill status={integrationStatus[item.statusKey].status} />}</span>
                        </button>
                      );
                    })}
                  </>
                )}

                {/* Logout at bottom */}
                <div style={{ borderTop: `1px solid ${sidebarBorder}` }}>
                  <button
                    onClick={() => { handleLogout(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                    style={{ color: "#DC2626" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#DC2626"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#DC2626"; }}
                    data-testid="logout-button"
                  >
                    <LogOut size={18} />
                    <span className="font-medium tracking-wide">LOGOUT</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6 md:p-8 lg:p-10" data-testid="main-content">{children}</main>
    </div>
  );
}
